/**
 * H5 唤起 App 及下载跳转
 *
 * 逻辑：
 * - iOS: 直接跳转 App Store
 * - 鸿蒙: 跳转华为应用市场（与华为手机同一应用生态）
 * - Android: 按机型/渠道跳转对应应用商店（华为、荣耀、小米、VIVO、OPPO、应用宝），否则走魔链聚合页
 *
 * 使用前请将 ANDROID_PACKAGE_NAME 替换为你们 App 的真实包名（如 com.xxx.Kukukao）
 */

// ---------- 通用 ----------
// Android 应用包名（必填：替换为实际上架时的包名）
const ANDROID_PACKAGE_NAME = 'com.zhixingda.kukukao'

// ---------- iOS ----------
const IOS_STORE_URL = 'https://apps.apple.com/cn/app/kukukao/id6751624430'

// ---------- 鸿蒙 / 华为应用市场 ----------
// 鸿蒙：使用 store scheme 跳转华为应用市场（应用 ID C115821963）
const HARMONYOS_STORE_URL = 'https://appgallery.huawei.com/app/detail?id=com.zhixingda.kukukao.hm'
// 华为应用市场（Scheme，华为 Android 机用）
const HUAWEI_STORE_URL = `appmarket://details?id=${ANDROID_PACKAGE_NAME}`

// ---------- Android 各应用商店 ----------
// 荣耀应用市场（使用 market 协议，由系统选择已安装商店；部分机型仍可用华为 scheme）
const HONOR_STORE_URL = `market://details?id=${ANDROID_PACKAGE_NAME}`
// 小米应用商店（Scheme）
const XIAOMI_STORE_URL = `mimarket://details?id=${ANDROID_PACKAGE_NAME}`
// VIVO 应用商店（Scheme）
const VIVO_STORE_URL = `vivomarket://details?id=${ANDROID_PACKAGE_NAME}`
// OPPO 软件商店（Scheme，参数名为 packagename）
const OPPO_STORE_URL = `oppomarket://details?packagename=${ANDROID_PACKAGE_NAME}`
// 应用宝 H5 详情页（用于微信/QQ 内置浏览器）
const TENCENT_YYB_URL = `https://sj.qq.com/appdetail/${ANDROID_PACKAGE_NAME}`
// 魔链聚合页（默认 Android 兜底，含应用宝/直接下载等）
const ANDROID_DOWNLOAD_URL = 'https://m.malink.cn/s/e63Ezy'

/**
 * 获取 UA 小写，便于检测
 */
function getUA() {
  return (navigator.userAgent || '').toLowerCase()
}

/**
 * 是否微信内置浏览器
 */
function isWechat() {
  return /micromessenger/i.test(navigator.userAgent)
}

/**
 * 是否 QQ 内置浏览器（可走应用宝）
 */
function isQQ() {
  return /qq\//i.test(navigator.userAgent) || /mqqbrowser/i.test(navigator.userAgent)
}

/**
 * 是否鸿蒙系统（HarmonyOS / ArkWeb / OpenHarmony）
 * 鸿蒙设备可能同时带 Android UA，需优先判断
 */
function isHarmonyOS() {
  const u = navigator.userAgent
  return u.indexOf('HarmonyOS') > -1 || u.indexOf('ArkWeb') > -1 || u.indexOf('OpenHarmony') > -1
}

/**
 * 是否华为机型（UA 通常含 HUAWEI，不含 HONOR）
 */
function isHuawei() {
  const u = getUA()
  return u.indexOf('huawei') > -1 && u.indexOf('honor') === -1
}

/**
 * 是否荣耀机型（UA 含 HONOR 或 honor）
 */
function isHonor() {
  return /honor/i.test(navigator.userAgent)
}

/**
 * 是否小米/红米（MIUI、Xiaomi、Redmi）
 */
function isXiaomi() {
  const u = getUA()
  return u.indexOf('mi ') > -1 || u.indexOf('xiaomi') > -1 || u.indexOf('redmi') > -1
}

/**
 * 是否 VIVO
 */
function isVivo() {
  return /vivo/i.test(navigator.userAgent)
}

/**
 * 是否 OPPO（含 realme 等子品牌可按需加入）
 */
function isOppo() {
  return /oppo/i.test(navigator.userAgent)
}

/** 微信/QQ 内打开应用市场时的提示文案 */
const OPEN_IN_BROWSER_TIP = '请点击右上角「…」选择「在浏览器中打开」后重新点击下载'

/**
 * 显示“用浏览器打开”提示（Toast，不阻塞跳转）
 */
function showOpenInBrowserTip() {
  if (typeof document === 'undefined' || !document.body) return
  const tip = document.createElement('div')
  tip.textContent = OPEN_IN_BROWSER_TIP
  tip.setAttribute('role', 'alert')
  Object.assign(tip.style, {
    position: 'fixed',
    left: '16px',
    right: '16px',
    bottom: 'calc(24px + env(safe-area-inset-bottom))',
    padding: '12px 16px',
    background: 'rgba(0,0,0,0.75)',
    color: '#fff',
    fontSize: '14px',
    lineHeight: '1.5',
    borderRadius: '8px',
    textAlign: 'center',
    zIndex: '99999',
    boxSizing: 'border-box'
  })
  document.body.appendChild(tip)
  setTimeout(() => {
    if (tip.parentNode) tip.parentNode.removeChild(tip)
  }, 4000)
}

/**
 * 唤起下载或应用商店
 * 优先级：iOS → App Store；鸿蒙 → 华为应用市场；Android 先按机型（华为/荣耀/小米/VIVO/OPPO）→ 再微信/QQ 内用应用宝 → 魔链兜底
 * 微信内也会按机型跳对应应用市场，仅「非上述机型」在微信/QQ 内才走应用宝
 */
export function openApp() {
  const u = navigator.userAgent
  const isAndroidDevice = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1
  const isIOSDevice = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)

  if (isIOSDevice) {
    window.location.href = IOS_STORE_URL
    return
  }

  // 鸿蒙系统：使用 store scheme 跳转华为应用市场
  if (isHarmonyOS()) {
    window.location.href = HARMONYOS_STORE_URL
    return
  }

  if (isAndroidDevice) {
    const inWechatOrQQ = isWechat() || isQQ()
    // 先按机型跳对应应用市场（含微信/QQ 内：VIVO 等也跳自家商店）
    if (isHuawei()) {
      if (inWechatOrQQ) showOpenInBrowserTip()
      window.location.href = HUAWEI_STORE_URL
      return
    }
    if (isHonor()) {
      if (inWechatOrQQ) showOpenInBrowserTip()
      window.location.href = HONOR_STORE_URL
      return
    }
    if (isXiaomi()) {
      if (inWechatOrQQ) showOpenInBrowserTip()
      window.location.href = XIAOMI_STORE_URL
      return
    }
    if (isVivo()) {
      if (inWechatOrQQ) showOpenInBrowserTip()
      window.location.href = VIVO_STORE_URL
      return
    }
    if (isOppo()) {
      if (inWechatOrQQ) showOpenInBrowserTip()
      window.location.href = OPPO_STORE_URL
      return
    }
    // 非上述机型：微信/QQ 内用应用宝，其他用魔链
    if (isWechat() || isQQ()) {
      window.location.href = TENCENT_YYB_URL
      return
    }
    window.location.href = ANDROID_DOWNLOAD_URL
    return
  }

  // 非手机或未知：默认跳 iOS 作为着陆页
  window.location.href = IOS_STORE_URL
}
